import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

function CommunityChatPanel({
  title,
  connectionState,
  messages,
  error,
  currentUser,
  isAdmin,
  onSend,
  onTogglePinned,
  onToggleMembers,
  onPinMessage,
}) {
  const [draft, setDraft] = useState('');
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages.length]);

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!draft.trim()) return;
    onSend(draft);
    setDraft('');
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSubmit(event);
    }
  };

  const statusLabel = (() => {
    if (connectionState === 'connected') return 'Connected';
    if (connectionState === 'connecting') return 'Connecting…';
    return 'Disconnected';
  })();

  const canSend = connectionState === 'connected' && draft.trim().length > 0;

  return (
    <section className="community-chat" aria-label={title}>
      <header className="community-chat__header">
        <div className="community-chat__title-group">
          <h1 className="community-chat__title">{title}</h1>
          <span
            className={`community-chat__status community-chat__status--${connectionState}`}
            aria-live="polite"
          >
            {statusLabel}
          </span>
        </div>
        <div className="community-chat__header-actions">
          <button
            type="button"
            className="community-chat__header-btn community-chat__header-btn--pin"
            onClick={onTogglePinned}
            aria-label="Show pinned messages"
            title="Pinned messages"
          >
            <PinIcon />
          </button>
          {onToggleMembers && (
            <button
              type="button"
              className="community-chat__header-btn"
              onClick={onToggleMembers}
              aria-label="Show participants"
              title="Participants"
            >
              <PeopleIcon />
            </button>
          )}
        </div>
      </header>

      <div ref={scrollRef} className="community-chat__messages">
        {error && (
          <div className="community-chat__error" role="alert">
            {error}
          </div>
        )}
        {!error && messages.length === 0 && (
          <div className="community-chat__empty">
            <p>No messages yet. Be the first to say hi.</p>
          </div>
        )}
        {messages.map((msg, index) => {
          const isMine = msg._isMine || msg.sender?.username === currentUser.username;
          const prevMsg = index > 0 ? messages[index - 1] : null;
          const showDateSeparator = !isSameDay(msg.timestamp, prevMsg?.timestamp);
          return (
            <React.Fragment key={msg.id}>
              {showDateSeparator && (
                <div className="community-chat__date-separator" role="separator">
                  <span>{formatDateSeparator(msg.timestamp)}</span>
                </div>
              )}
              <div className={`community-msg ${isMine ? 'community-msg--mine' : ''}`}>
                <div className="community-msg__avatar-col">
                  {msg.sender?.profileImage ? (
                    <img className="community-msg__avatar" src={msg.sender.profileImage} alt="" />
                  ) : (
                    <span className="community-msg__avatar community-msg__avatar--fallback">
                      {(msg.sender?.username || '?').charAt(0).toUpperCase()}
                    </span>
                  )}
                </div>
                <div className="community-msg__body">
                  <div className="community-msg__meta">
                    <span className="community-msg__name">{msg.sender?.username || 'unknown'}</span>
                    <span className="community-msg__time">{formatTimestamp(msg.timestamp)}</span>
                  </div>
                  <div className="community-msg__content">{msg.content}</div>
                </div>
                {isAdmin && (
                  <button
                    type="button"
                    className="community-msg__pin-btn"
                    onClick={() => onPinMessage(msg._raw || msg)}
                    title="Pin message"
                    aria-label="Pin message"
                  >
                    <PinIcon small />
                  </button>
                )}
              </div>
            </React.Fragment>
          );
        })}
      </div>

      <form className="community-chat__composer" onSubmit={handleSubmit}>
        <textarea
          className="community-chat__input"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={
            connectionState === 'connected' ? 'Message' : 'Connecting to chat…'
          }
          rows={1}
          aria-label="Message"
          disabled={connectionState !== 'connected'}
        />
        <button
          type="submit"
          className="community-chat__send"
          disabled={!canSend}
          aria-label="Send message"
        >
          <SendIcon />
        </button>
      </form>
    </section>
  );
}

function PeopleIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  );
}

function PinIcon({ small = false }) {
  const size = small ? 14 : 18;
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <line x1="12" y1="17" x2="12" y2="22" />
      <path d="M5 17h14l-3-5V6a1 1 0 0 0-1-1H9a1 1 0 0 0-1 1v6l-3 5z" />
    </svg>
  );
}

function SendIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <line x1="22" y1="2" x2="11" y2="13" />
      <polygon points="22 2 15 22 11 13 2 9 22 2" />
    </svg>
  );
}

function formatTimestamp(value) {
  if (!value) return '';
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}

function isSameDay(a, b) {
  if (!a || !b) return false;
  const da = a instanceof Date ? a : new Date(a);
  const db = b instanceof Date ? b : new Date(b);
  if (Number.isNaN(da.getTime()) || Number.isNaN(db.getTime())) return false;
  return da.toDateString() === db.toDateString();
}

function formatDateSeparator(value) {
  if (!value) return '';
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return '';

  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);

  if (isSameDay(d, today)) return 'Today';
  if (isSameDay(d, yesterday)) return 'Yesterday';

  const weekAgo = new Date(today);
  weekAgo.setDate(today.getDate() - 7);
  if (d > weekAgo) return d.toLocaleDateString([], { weekday: 'long' });

  return d.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
}

CommunityChatPanel.propTypes = {
  title: PropTypes.string.isRequired,
  connectionState: PropTypes.oneOf(['disconnected', 'connecting', 'connected']).isRequired,
  messages: PropTypes.array.isRequired,
  error: PropTypes.string,
  currentUser: PropTypes.object.isRequired,
  isAdmin: PropTypes.bool,
  onSend: PropTypes.func.isRequired,
  onTogglePinned: PropTypes.func.isRequired,
  onToggleMembers: PropTypes.func,
  onPinMessage: PropTypes.func.isRequired,
};

export default CommunityChatPanel;
